import React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { cn, ConfirmDialog, showToast } from '@ideoz/ui';
import { INTEGRATIONS, getIntegration } from './data/catalog';
import { useIntegrations } from './state/useIntegrations';
import { IntegrationCard } from './components/IntegrationCard';

type Filter = 'all' | 'connected' | 'available';

const FILTER_IDS: Filter[] = ['all', 'connected', 'available'];

/**
 * Integrations list — search + connected/available filter over the catalog of
 * tools Nexus can connect to. Connect / Disconnect happen inline on each card.
 */
export function IntegrationsModule(): JSX.Element {
  const { t } = useTranslation();
  const { isConnected, getConnection, connect, disconnect } = useIntegrations();

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  // The integration currently mid-connect/disconnect (its catalog id).
  const [busyId, setBusyId] = useState<string | null>(null);
  // The integration awaiting disconnect confirmation.
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const handleConnect = async (id: string) => {
    setBusyId(id);
    try {
      // Same-tab navigation to the broker consent flow — we don't return here.
      await connect(id);
    } catch {
      showToast({
        message: t('integrations.toast.connectFailed', { name: getIntegration(id)?.name }),
        severity: 'error',
      });
      setBusyId(null);
    }
  };

  const handleDisconnect = async (id: string) => {
    const connection = getConnection(id);
    if (!connection) return;
    setBusyId(id);
    try {
      await disconnect(connection.id);
      showToast({
        message: t('integrations.toast.disconnected', { name: getIntegration(id)?.name }),
        severity: 'success',
      });
    } catch {
      showToast({
        message: t('integrations.toast.disconnectFailed', { name: getIntegration(id)?.name }),
        severity: 'error',
      });
    } finally {
      setBusyId(null);
    }
  };

  const confirmIntegration = confirmId ? getIntegration(confirmId) : null;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return INTEGRATIONS.filter((i) => {
      const connected = isConnected(i.id);
      if (filter === 'connected' && !connected) return false;
      if (filter === 'available' && connected) return false;
      if (!q) return true;
      return [i.name, i.category, i.summary].some((s) => s.toLowerCase().includes(q));
    });
  }, [query, filter, isConnected]);

  return (
    <section className="mx-auto w-full max-w-5xl px-6 py-10">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
            {t('integrations.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('integrations.description')}
          </p>
        </div>
        <div className="relative w-full max-w-xs shrink-0">
          <Search
            size={16}
            className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('integrations.searchTools')}
            aria-label={t('integrations.searchTools')}
            className="w-full rounded-xl border border-gray-200 bg-white py-2.5 ps-9 pe-3 text-sm text-gray-800 outline-none placeholder:text-gray-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-300 dark:border-gray-700 dark:bg-ink-surface dark:text-gray-100"
          />
        </div>
      </header>

      <div className="mb-5 flex flex-col gap-4">
        <div className="flex gap-2">
          {FILTER_IDS.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setFilter(id)}
              className={cn(
                'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
                filter === id
                  ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                  : 'border border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
              )}
            >
              {t(`integrations.filters.${id}`)}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-400">{t('integrations.noResults')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {visible.map((integration) => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              connected={isConnected(integration.id)}
              busy={busyId === integration.id}
              onConnect={() => handleConnect(integration.id)}
              onDisconnect={() => setConfirmId(integration.id)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmIntegration}
        variant="danger"
        title={confirmIntegration ? t('integrations.confirm.title', { name: confirmIntegration.name }) : ''}
        description={
          confirmIntegration
            ? t('integrations.confirm.desc', { name: confirmIntegration.name })
            : ''
        }
        confirmLabel={t('integrations.disconnect')}
        cancelLabel={t('integrations.cancel')}
        onConfirm={() => {
          const id = confirmId;
          setConfirmId(null);
          if (id) void handleDisconnect(id);
        }}
        onCancel={() => setConfirmId(null)}
      />
    </section>
  );
}
